import { CaretDown, CaretRight, CaretUp, Check } from "@phosphor-icons/react"
import { cn } from "@tokenterminal/core/utils/cn"
import { type ReactNode, useContext } from "react"
import {
  Header as AriaHeader,
  MenuItem as AriaMenuItem,
  type MenuItemProps as AriaMenuItemProps,
  MenuSection as AriaMenuSection,
  Separator as AriaSeparator,
  Menu,
  type MenuProps,
  type MenuSectionProps,
  MenuTrigger,
  type MenuTriggerProps,
  OverlayTriggerStateContext,
  Popover,
  type PopoverProps,
  SubmenuTrigger,
} from "react-aria-components"
import { Icon } from "../icon"
import type { IconProps } from "../icon/Icon"

export const MenuSection = ({
  className,
  ...props
}: MenuSectionProps<object>) => (
  <AriaMenuSection className={cn("tt-menu-section", className)} {...props} />
)

export const Header = ({
  className,
  ...props
}: {
  children: React.ReactNode
  className?: string
}) => <AriaHeader className={cn("tt-section-header", className)} {...props} />

export const Separator = ({ className }: { className?: string }) => (
  <AriaSeparator className={cn("tt-separator", className)} />
)

export type MenuItemProps = AriaMenuItemProps & {
  icon?: IconProps["as"]
  /** The callback to call when the item is selected. */
  onAction?: AriaMenuItemProps["onAction"]
  /** Whether to close the menu when the item is selected. */
  closeOnSelect?: boolean
  className?: string
  hideSubmenuIcon?: boolean
}

export const MenuItem = ({
  icon,
  className,
  hideSubmenuIcon,
  ...props
}: MenuItemProps) => {
  const textValue =
    props.textValue ||
    (typeof props.children === "string" ? props.children : undefined)

  return (
    <AriaMenuItem
      textValue={textValue}
      className={({ isFocused, isSelected, isOpen }) =>
        cn(
          "tt-menu-item",
          isFocused && "focused",
          isSelected && "selected",
          isOpen && "open",
          className
        )
      }
      {...props}
    >
      {(renderProps) => (
        <div className="flex flex-row w-full items-center gap-2">
          {icon ? (
            <Icon as={icon} className="text-fg-secondary" />
          ) : (
            <Icon as={Check} className="tt-selected-icon text-fg-secondary" />
          )}
          {/* @ts-ignore - Fix children prop */}
          <div className="flex-1">
            {typeof props.children === "function"
              ? props.children(renderProps)
              : props.children}
          </div>
          {renderProps.hasSubmenu && !hideSubmenuIcon && (
            <Icon as={CaretRight} />
          )}
        </div>
      )}
    </AriaMenuItem>
  )
}

export const DropdownIndicator = ({
  openIcon = CaretUp,
  closedIcon = CaretDown,
  ...props
}: Omit<IconProps, "as"> & {
  openIcon?: IconProps["as"]
  closedIcon?: IconProps["as"]
}) => {
  const context = useContext(OverlayTriggerStateContext)
  return <Icon as={context?.isOpen ? openIcon : closedIcon} {...props} />
}

const MenuPopover = ({
  children,
  className,
  ...props
}: Omit<PopoverProps, "className"> & { className?: string }) => {
  return (
    <Popover className={cn("tt-popover", className)} {...props}>
      {children}
    </Popover>
  )
}

const MenuButtonMenu = ({ children, ...props }: MenuProps<object>) => {
  return (
    <Menu className="tt-menu" {...props}>
      {children}
    </Menu>
  )
}

export type MenuButtonProps<T> = MenuProps<T> &
  Omit<MenuTriggerProps, "children" | "trigger"> & {
    trigger: ReactNode
    placement?: PopoverProps["placement"]
    popoverProps?: Omit<PopoverProps, "className"> & { className?: string }
  }

/**
 * A button that triggers a menu when clicked. Helpful for dropdowns of actions or settings.
 * The menu can contain any number of items, sections, and headers.
 *
 * ```tsx
 * import { MenuButton, MenuItem, MenuSection, Separator } from "@tokenterminal/core/ui/menu/MenuButton"
 *
 * <MenuButton label="Actions">
 *   <MenuItem onAction={...}>Item 1</MenuItem>
 *   <MenuItem onAction={...}>Item 2</MenuItem>
 *   <Separator />
 *   <MenuSection>
 *     <Header>Section 1</Header>
 *     <MenuItem onAction={...}>Item 3</MenuItem>
 *   </MenuSection>
 * </MenuButton>
 * ```
 */

export const MenuButton = ({
  children,
  trigger,
  placement,
  popoverProps,
  ...props
}: MenuButtonProps<object>) => {
  return (
    <MenuTrigger {...props}>
      {trigger}
      <MenuPopover placement={placement} {...popoverProps}>
        <MenuButtonMenu {...props}>{children}</MenuButtonMenu>
      </MenuPopover>
    </MenuTrigger>
  )
}

MenuButton.Item = MenuItem
MenuButton.Separator = Separator
MenuButton.Section = MenuSection
MenuButton.Header = Header
MenuButton.Popover = MenuPopover
MenuButton.SubmenuTrigger = SubmenuTrigger
MenuButton.Menu = MenuButtonMenu
MenuButton.DropdownIndicator = DropdownIndicator
